import {useEffect, useRef, useState} from 'react';
import {ITodo} from '@app-types/websocket/todo.types';
import {isTodoOnScreen} from './todoVisibility';

export const TODO_SEEN_DELAY = 1000;
export const TODO_SEEN_HIGHLIGHT_DURATION = 2400;

interface IOptions {
  todo: ITodo;
  myAccountId: number;
  active: boolean;
  seen: boolean;
  onSeen: () => void;
  revisionKey?: string;
}

export const useTodoSeen = (row: React.RefObject<HTMLDivElement>, options: IOptions) => {
  const {todo, myAccountId, active, seen, onSeen} = options;
  // Reader changes do not create a new version, but edits to the task do.
  const revision = options.revisionKey || JSON.stringify([
    todo.id, todo.title, todo.description, todo.dueDate, todo.assigned, todo.visible,
    todo.renewal, todo.handovergroup, todo.scheduleId, todo.completedBy, todo.completionTime,
  ]);
  const previous = useRef({revision, myAccountId, seen});
  const sent = useRef(false);
  const sendSeen = useRef(onSeen);
  sendSeen.current = onSeen;
  const [visible, setVisible] = useState(false);
  const [recentlySeen, setRecentlySeen] = useState(false);

  useEffect(() => {
    const sameTask = previous.current.revision === revision && previous.current.myAccountId === myAccountId;
    const acknowledged = sameTask && !previous.current.seen && seen;
    if (!sameTask || (previous.current.seen && !seen)) {
      sent.current = false;
    }
    previous.current = {revision, myAccountId, seen};

    let dwellTimer: number | undefined;
    let highlightTimer: number | undefined;
    let frame: number | undefined;
    const justSeen = acknowledged && active && isTodoOnScreen(row.current);
    setRecentlySeen(justSeen);
    if (justSeen) {
      highlightTimer = window.setTimeout(() => setRecentlySeen(false), TODO_SEEN_HIGHLIGHT_DURATION);
    }

    const cancelDwell = () => {
      if (dwellTimer !== undefined) {
        window.clearTimeout(dwellTimer);
        dwellTimer = undefined;
      }
    };

    const checkVisibility = () => {
      const onScreen = active && isTodoOnScreen(row.current);
      setVisible(onScreen);
      if (!onScreen || seen || sent.current) {
        cancelDwell();
        return;
      }
      if (dwellTimer === undefined) {
        dwellTimer = window.setTimeout(() => {
          dwellTimer = undefined;
          if (active && isTodoOnScreen(row.current)) {
            sent.current = true;
            sendSeen.current();
          } else {
            checkVisibility();
          }
        }, TODO_SEEN_DELAY);
      }
    };

    const scheduleCheck = () => {
      // Cancel immediately in the background; coalesce scrolling and layout
      // notifications into one visibility check per frame.
      if (document.visibilityState === 'hidden') {
        cancelDwell();
        setVisible(false);
        return;
      }
      if (frame === undefined) {
        frame = window.requestAnimationFrame(() => {
          frame = undefined;
          checkVisibility();
        });
      }
    };

    checkVisibility();
    const observer = active && typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(scheduleCheck, {threshold: [0, 0.5, 1]})
      : undefined;
    if (observer && row.current) {
      observer.observe(row.current);
    }
    if (active) {
      document.addEventListener('scroll', scheduleCheck, true);
      document.addEventListener('visibilitychange', scheduleCheck);
      document.addEventListener('click', scheduleCheck, true);
      document.addEventListener('transitionend', scheduleCheck, true);
      window.addEventListener('resize', scheduleCheck);
      window.addEventListener('focus', scheduleCheck);
    }

    return () => {
      cancelDwell();
      window.clearTimeout(highlightTimer);
      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
      }
      if (observer) {
        observer.disconnect();
      }
      document.removeEventListener('scroll', scheduleCheck, true);
      document.removeEventListener('visibilitychange', scheduleCheck);
      document.removeEventListener('click', scheduleCheck, true);
      document.removeEventListener('transitionend', scheduleCheck, true);
      window.removeEventListener('resize', scheduleCheck);
      window.removeEventListener('focus', scheduleCheck);
    };
  }, [active, myAccountId, revision, row, seen]);

  return {visible, recentlySeen};
};



// WEBPACK FOOTER //
// ./src/components/todo/useTodoSeen.ts